import { useEffect, useState } from 'react';
import {
  clearInviteFromUrl,
  fetchAuthStatus,
  loginPasskey,
  loginWithPassword,
  passkeySupported,
  peekInvite,
  readInviteTokenFromUrl,
  registerPasskey,
  registerWithPassword,
  type AuthStatus,
  type AuthUser,
} from '../auth/passkey';

export interface AuthFormProps {
  onAuthed: (user: AuthUser) => void;
  onError?: (message: string) => void;
  onInfo?: (message: string, tone?: 'success' | 'error') => void;
  /** コンパクト表示（設定画面用） */
  compact?: boolean;
}

export function AuthForm({ onAuthed, onError, onInfo, compact }: AuthFormProps) {
  const [authBusy, setAuthBusy] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [inviteToken, setInviteToken] = useState<string | null>(null);
  const [authStatus, setAuthStatus] = useState<AuthStatus | null>(null);
  const canPasskey = passkeySupported();
  const canRegister = !authStatus?.inviteRequired || Boolean(inviteToken);

  useEffect(() => {
    void fetchAuthStatus().then(setAuthStatus);
    const token = readInviteTokenFromUrl();
    if (!token) return;
    setInviteToken(token);
    void peekInvite(token).then((inv) => {
      if (inv?.email) {
        setUsername(inv.email);
        onInfo?.(`${inv.email} への招待を受け取りました`, 'success');
      } else {
        onInfo?.('招待リンクが無効か期限切れです', 'error');
        setInviteToken(null);
        clearInviteFromUrl();
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const run = (fn: () => Promise<AuthUser>, success: string) => {
    void (async () => {
      setAuthBusy(true);
      try {
        const u = await fn();
        setPassword('');
        clearInviteFromUrl();
        setInviteToken(null);
        onInfo?.(success, 'success');
        onAuthed(u);
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        onError?.(msg);
        onInfo?.(msg, 'error');
      } finally {
        setAuthBusy(false);
      }
    })();
  };

  const field =
    'mt-1 w-full rounded-lg border border-base-600 bg-base-900 px-3 py-2 text-sm text-slate-100 outline-none focus:border-accent';

  return (
    <div className={compact ? 'space-y-3' : 'mx-auto w-full max-w-sm space-y-3'}>
      {authStatus?.inviteRequired && !inviteToken && (
        <p className="text-xs text-amber-300/90">
          新規登録は招待制です。管理者から届いたリンクを開いてください。既存アカウントはログインできます。
        </p>
      )}
      {authStatus && !authStatus.inviteRequired && (
        <p className="text-xs text-slate-400">登録は無料です。クレジットカードは要りません。</p>
      )}
      {inviteToken && <p className="text-xs text-emerald-300/90">招待リンク経由の登録モードです。</p>}
      {authStatus?.bootstrapOpen && (
        <p className="text-xs text-emerald-300/90">最初のアカウントは管理者になります。</p>
      )}

      <label className="block text-xs text-slate-400">
        メール / ユーザー名
        <input
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          autoComplete="username"
          placeholder="you@example.com"
          className={field}
        />
      </label>
      <label className="block text-xs text-slate-400">
        パスワード
        <input
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          type="password"
          autoComplete={canRegister ? 'new-password' : 'current-password'}
          className={field}
        />
      </label>
      {canRegister && (
        <label className="block text-xs text-slate-400">
          表示名（任意）
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            autoComplete="nickname"
            className={field}
          />
        </label>
      )}

      <button
        type="button"
        className="btn-primary w-full"
        disabled={authBusy || !username.trim() || !password}
        onClick={() =>
          run(() => loginWithPassword(username.trim(), password), 'サインインしました')
        }
      >
        パスワードでログイン
      </button>

      {canRegister && (
        <button
          type="button"
          className="btn-ghost w-full"
          disabled={authBusy || !username.trim() || !password}
          onClick={() =>
            run(
              () =>
                registerWithPassword({
                  username: username.trim(),
                  password,
                  displayName: displayName.trim() || undefined,
                  inviteToken: inviteToken || undefined,
                }),
              'アカウントを作成しました',
            )
          }
        >
          パスワードで新規登録
        </button>
      )}

      {canPasskey && (
        <>
          <button
            type="button"
            className="btn-ghost w-full"
            disabled={authBusy}
            onClick={() =>
              run(() => loginPasskey(username.trim() || undefined), 'Passkey でサインインしました')
            }
          >
            Passkey でログイン
          </button>
          {canRegister && (
            <button
              type="button"
              className="btn-ghost w-full"
              disabled={authBusy || !username.trim()}
              onClick={() =>
                run(
                  () =>
                    registerPasskey(
                      username.trim(),
                      displayName.trim() || undefined,
                      inviteToken || undefined,
                    ),
                  'Passkey を登録しました',
                )
              }
            >
              Passkey で新規登録
            </button>
          )}
        </>
      )}
    </div>
  );
}
